import { useCallback, useMemo } from 'react';

import { useFetchCutoutLaborQuery } from '../cutoutLaborService';

import {
  DEFAULT_COOKTOP_QTECODEID,
  MATERIAL_CUTOUT_DEFAULTS,
  QTEType,
} from '~/core/constants';
import { ProductType } from '~/core/enums';
import { CutoutLaborModel, ProductDetails } from '~/core/models';
import { ApplianceModel } from '~/core/models';
import { buildQteAttributes } from '~/core/utils';
import {
  TransformProductToProductDetailsCallbackType,
  createFetchProductsHook,
} from '~/core/utils/createFetchProductsHook';

const selectProductsAsProductDetails: TransformProductToProductDetailsCallbackType<
  CutoutLaborModel
> = (cutoutLaborProducts) =>
  cutoutLaborProducts.map((item) => {
    const detail: ProductDetails = {
      productId: item.productId,
      name: item.name,
      consumerDesc: item.consumerDesc,
      productType: ProductType.SINK_LABOR,
      price: item.price,
      priceWithoutDiscount: item.priceWithoutDiscount, // no discount in model
      promos: item.promos ? [...item.promos] : [],
      abbrevDesc: item.abbrevDesc,
      unitOfMeasure: item.unitOfMeasure,
      ...buildQteAttributes(item),
    };

    return detail;
  });

export const useCutoutLaborProducts = createFetchProductsHook<
  CutoutLaborModel,
  TransformProductToProductDetailsCallbackType<CutoutLaborModel>
>({
  apiQueryHook: useFetchCutoutLaborQuery,
  selectProductsAsProductDetails,
});

export interface SelectSinkCutoutProps {
  materialType: string | undefined | null;
  sink: ApplianceModel;
  _source?: string;
}

export interface UseSelectedCutoutLaborResponse {
  cooktopCutout: CutoutLaborModel | undefined;
  selectSinkCutout: (
    props: SelectSinkCutoutProps
  ) => CutoutLaborModel | undefined;
}

export const useSelectCutoutLabor = (): UseSelectedCutoutLaborResponse => {
  const { data: cutoutLaborProducts } = useCutoutLaborProducts();

  const cooktopCutout = useMemo(
    () =>
      cutoutLaborProducts.find(
        (product) => product.qteCodeId === DEFAULT_COOKTOP_QTECODEID
      ),
    [cutoutLaborProducts]
  );

  const selectSinkCutout = useCallback(
    (props: SelectSinkCutoutProps): CutoutLaborModel | undefined => {
      const { materialType, sink } = props;

      const directCutoutMatch = cutoutLaborProducts
        .filter(
          (product) =>
            sink.qteTypeId === QTEType.Sink &&
            product.qteCodeId === sink.qteCodeId
        )
        .sort((a, b) => b.price - a.price || b.productId - a.productId) // price high-low then id high-low
        .at(0);

      if (directCutoutMatch) {
        return directCutoutMatch;
      }

      // If no direct match, search using MATERIAL_CUTOUT_DEFAULTS rules
      const fallbackCutout = MATERIAL_CUTOUT_DEFAULTS.find(
        (rule) =>
          !rule.materialType ||
          new RegExp(rule.materialType).test(materialType ?? '')
      );

      // If a fallback rule is found, try to find the corresponding cutout product
      if (fallbackCutout) {
        const fallbackCutoutMatch = cutoutLaborProducts.find(
          (product) => product.qteCodeId === fallbackCutout.cutoutQteCodeId
        );

        if (fallbackCutoutMatch) {
          return fallbackCutoutMatch;
        }
      }

      return undefined;
    },
    [cutoutLaborProducts]
  );

  return {
    cooktopCutout,
    selectSinkCutout,
  };
};
